import type { FC } from 'react';

import { useTranslation } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { colors, PrimaryButton } from '@crehana/ui';
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
} from 'recharts';

import { StyledResponsiveContainer } from './SkillsGraph.styles';
import type { SkillsGraphProps } from './types';

const { primaryLight, primaryLighter56 } = colors;

const SkillsGraph: FC<React.PropsWithChildren<SkillsGraphProps>> = ({
  data,
  error,
  refetch,
}) => {
  const { t } = useTranslation();

  const renderGraph = () => {
    if (error) {
      return (
        <div className="flex h-full justify-center flex-col items-center text-center py-16 px-16 sm:py-32 sm:px-0">
          <h4 className="font-h4 text-white mb-16 text-center">
            {t('RESULTS_ERROR')}
          </h4>
          {error && error.message ? (
            <p className="font-body3 text-red-main mb-16">{error.message}</p>
          ) : (
            <p className="font-body3 text-white mb-16">
              {t('SKILLS_GRAPH_ERROR_BODY')}
            </p>
          )}
          <PrimaryButton
            label={t('SKILLS_GRAPH_UPDATE_CTA')}
            size="s"
            onClick={refetch}
            darkMode
          />
        </div>
      );
    }
    if (data.length === 0) {
      return (
        <div className="flex h-full justify-center items-center">
          <p className="font-subtitle2 text-center text-white whitespace-pre-line">
            {t('SKILLS_GRAPH_EMPTY_CASE')}
          </p>
        </div>
      );
    }
    return (
      <StyledResponsiveContainer width="100%" height={320} isDark>
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={data}>
          <PolarGrid />
          <PolarAngleAxis dataKey="subject" className="font-subtitle3" />
          <PolarRadiusAxis
            domain={[0, data[0]?.total || 20]}
            tick={false}
            axisLine={false}
          />
          <Radar
            name={t('SKILLS_GRAPH_ME')}
            dataKey="score"
            stroke={primaryLighter56}
            fill={primaryLighter56}
            fillOpacity={1}
          />
          <Radar
            name={t('SKILLS_GRAPH_TEAM')}
            dataKey="comparativeScore"
            stroke={primaryLight}
            fill={primaryLight}
            fillOpacity={0.6}
          />
          <Legend />
        </RadarChart>
      </StyledResponsiveContainer>
    );
  };

  return (
    <section className="bg-base-light-dark-mode flex flex-col p-24 h-full rounded-5">
      <p className="text-white text-center font-subtitle2 mb-32">
        {t('SKILLS_GRAPH_TITLE')}
      </p>
      {renderGraph()}
    </section>
  );
};

export default SkillsGraph;
